import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";
import EmptyState from "../../../components/shared/EmptyState";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";
import { toast } from "../../../components/shared/toast";

const SCALE_OPTIONS = [3, 5, 10];

const blankAttr = () => ({ name: "", description: "", weight: 0 });

export default function Rubrics() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | {existing template}
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    document.title = "Rubrics - Ample Hire";
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const r = await axiosInstance.get("/job-posting/scorecards/templates");
      setTemplates(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.message || "Failed to load templates");
      setErrorStatus(e?.status || e?.response?.status || 500);
    } finally {
      setLoading(false);
    }
  };

  const onSave = async (form) => {
    setSaveError(null);
    try {
      const body = {
        ...(editing && editing !== "new" ? editing : {}),
        name: form.name,
        attributes: JSON.stringify(form.attributes),
        ratingScale: form.ratingScale,
        includeRecommendation: form.includeRecommendation,
      };
      if (editing === "new" || editing?.id == null || editing?.id < 0) {
        await axiosInstance.post("/job-posting/scorecards/templates", body);
      } else {
        await axiosInstance.put(`/job-posting/scorecards/templates/${editing.id}`, body);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setSaveError(e?.data?.error || e?.message || "Save failed");
    }
  };

  const onArchive = async (id) => {
    if (id == null || id < 0) return;
    if (!window.confirm("Archive this rubric?")) return;
    try {
      await axiosInstance.delete(`/job-posting/scorecards/templates/${id}`);
      toast.success("Rubric archived");
      await load();
    } catch (e) {
      toast.error("Archive failed", { description: e?.message || "Unknown error" });
    }
  };

  return (
    <div className="page-enter" style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 32px" }}>
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Rubrics</h1>
          <p className="text-sm text-vf-muted mt-1">
            Define weighted attribute rubrics for interviewer scorecards.
          </p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm font-medium"
        >
          + New rubric
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <LoadingState variant="skeleton" shape="card" count={3} />
        </div>
      ) : error ? (
        <ErrorState
          status={errorStatus}
          description={errorStatus === 403 ? undefined : error}
          retry={load}
        />
      ) : templates.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl">
          <EmptyState
            title="No rubrics yet"
            description="Define a weighted attribute rubric to standardize how interviewers score candidates."
            primaryAction={{ label: "+ New rubric", onClick: () => setEditing("new") }}
          />
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => (
            <RubricCard
              key={t.id}
              t={t}
              onEdit={() => setEditing(t)}
              onArchive={() => onArchive(t.id)}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <RubricEditor
            initial={editing === "new" ? null : editing}
            onCancel={() => { setEditing(null); setSaveError(null); }}
            onSave={onSave}
            saveError={saveError}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function RubricCard({ t, onEdit, onArchive }) {
  const attrs = useMemo(() => parseAttrs(t.attributes), [t.attributes]);
  const isSeed = t.id == null || t.id < 0;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <div className="font-semibold text-vf-base">{t.name || "Untitled"}</div>
          {isSeed && (
            <span className="text-[10px] uppercase font-semibold text-vf-muted bg-slate-100 px-1.5 py-0.5 rounded">
              default (not yet saved)
            </span>
          )}
        </div>
        <div className="text-xs text-vf-muted mt-0.5">
          {attrs.length} attribute{attrs.length === 1 ? "" : "s"} · {t.ratingScale ?? 5}-point scale
          {t.includeRecommendation ? " · includes recommendation" : ""}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {attrs.map((a) => (
            <span key={a.name} className="text-[11px] bg-vf-tint text-vf-primary px-1.5 py-0.5 rounded font-medium">
              {a.name} · {a.weight}%
            </span>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={onEdit} className="px-2.5 py-1 rounded border border-slate-300 text-xs font-medium">
          {isSeed ? "Customize" : "Edit"}
        </button>
        {!isSeed && (
          <button onClick={onArchive} className="px-2.5 py-1 rounded border border-red-300 text-red-700 text-xs font-medium hover:bg-red-50">
            Archive
          </button>
        )}
      </div>
    </div>
  );
}

function RubricEditor({ initial, onCancel, onSave, saveError }) {
  const [name, setName] = useState(initial?.name || "");
  const [ratingScale, setRatingScale] = useState(initial?.ratingScale || 5);
  const [includeRecommendation, setIncludeRecommendation] = useState(initial?.includeRecommendation ?? true);
  const [attributes, setAttributes] = useState(parseAttrs(initial?.attributes) || [blankAttr()]);

  const sum = useMemo(
    () => attributes.reduce((s, a) => s + (Number(a.weight) || 0), 0),
    [attributes],
  );
  const valid = Math.abs(sum - 100) < 0.01
    && name.trim()
    && attributes.length > 0
    && attributes.every((a) => a.name.trim());

  const updateAttr = (i, patch) =>
    setAttributes(attributes.map((a, j) => (i === j ? { ...a, ...patch } : a)));
  const addAttr = () => setAttributes([...attributes, blankAttr()]);
  const removeAttr = (i) => setAttributes(attributes.filter((_, j) => j !== i));

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onCancel}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 90 }}
      />
      <motion.aside
        initial={{ x: 520 }} animate={{ x: 0 }} exit={{ x: 520 }}
        transition={{ type: "tween", duration: 0.22 }}
        style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 520, background: "white", zIndex: 100, display: "flex", flexDirection: "column", boxShadow: "-12px 0 32px rgba(15,23,42,0.12)" }}
      >
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-vf-base">{initial ? "Edit rubric" : "New rubric"}</div>
            <div className="text-[11px] text-vf-muted mt-0.5">Weights across attributes must sum to 100.</div>
          </div>
          <button onClick={onCancel} className="w-6 h-6 text-slate-500 hover:bg-slate-100 rounded" aria-label="Close">×</button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Senior Engineer Rubric"
              className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Rating scale">
              <select
                value={ratingScale}
                onChange={(e) => setRatingScale(Number(e.target.value))}
                className="w-full px-2.5 py-1.5 border border-slate-300 rounded text-sm bg-white"
              >
                {SCALE_OPTIONS.map((s) => (
                  <option key={s} value={s}>1–{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Include recommendation">
              <label className="flex items-center gap-2 text-sm text-vf-base h-[34px]">
                <input
                  type="checkbox"
                  checked={includeRecommendation}
                  onChange={(e) => setIncludeRecommendation(e.target.checked)}
                />
                Show Strong yes / Yes / No / Strong no
              </label>
            </Field>
          </div>

          <div>
            <div className="flex items-baseline justify-between mb-1">
              <Label>Attributes</Label>
              <div className={`text-xs font-semibold ${Math.abs(sum - 100) < 0.01 ? "text-emerald-700" : "text-red-700"}`}>
                Sum: {sum.toFixed(1)} / 100
              </div>
            </div>
            <div className="space-y-2">
              {attributes.map((a, i) => (
                <div key={i} className="border border-slate-200 rounded p-2 space-y-1.5 bg-slate-50/40">
                  <div className="flex gap-2">
                    <input
                      value={a.name}
                      onChange={(e) => updateAttr(i, { name: e.target.value })}
                      placeholder="Attribute name"
                      className="flex-1 px-2 py-1 border border-slate-300 rounded text-xs"
                    />
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={a.weight}
                        onChange={(e) => updateAttr(i, { weight: Number(e.target.value) || 0 })}
                        className="w-16 px-2 py-1 border border-slate-300 rounded text-xs text-right tabular-nums"
                      />
                      <span className="text-[11px] text-vf-muted">%</span>
                    </div>
                    <button
                      onClick={() => removeAttr(i)}
                      disabled={attributes.length <= 1}
                      className="text-xs text-red-700 px-2 disabled:text-slate-300"
                    >
                      ×
                    </button>
                  </div>
                  <input
                    value={a.description || ""}
                    onChange={(e) => updateAttr(i, { description: e.target.value })}
                    placeholder="Optional description"
                    className="w-full px-2 py-1 border border-slate-200 rounded text-[11px] text-vf-muted"
                  />
                </div>
              ))}
              <button onClick={addAttr} className="text-xs text-vf-primary font-medium">+ Add attribute</button>
            </div>
          </div>

          {saveError && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{saveError}</div>
          )}
        </div>

        <footer className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
          <button onClick={onCancel} className="px-3 py-1.5 rounded border border-slate-300 text-xs">Cancel</button>
          <button
            onClick={() => onSave({ name, ratingScale, includeRecommendation, attributes })}
            disabled={!valid}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save rubric
          </button>
        </footer>
      </motion.aside>
    </>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label>{label}</Label>
    {children}
  </div>
);
const Label = ({ children }) => (
  <div className="text-[10px] uppercase tracking-wide text-vf-muted font-semibold mb-1">{children}</div>
);

function parseAttrs(attributesJson) {
  if (!attributesJson) return [];
  if (Array.isArray(attributesJson)) return attributesJson;
  try {
    const parsed = JSON.parse(attributesJson);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
